import React, { useRef, useEffect } from 'react';
import { CSVLink } from 'react-csv';
import { Button, QueryBuilderType } from '@scale/scaleui';
import { fetchAllSearchResults, emailResumeSearchResults, logResumeSearchEvent } from './api';
import { useMutation } from 'react-query';
import { MAX_AD_HOC_CSV_SIZE } from 'react-scale/components/GrowthCenter/ResumeSearch/constants';
import { useUser } from 'react-scale/components/ui/UserContext';
import { toast } from 'react-toastify';
import { ResumeSearchEventType } from '@scale/shared-types';

type ExportAllResultsButtonProps = {
  queries: QueryBuilderType;
  count: number;
  ids?: string[];
};

export const ExportAllResultsButton = ({ queries, count, ids }: ExportAllResultsButtonProps) => {
  const user = useUser();

  const filename = `resume-search-all-results-${new Date().toISOString()}.csv`;
  const csvHeaders = [
    { label: 'Contributor Id', key: 'contributorId' },
    { label: 'Email', key: 'email' },
    { label: 'Worker Team', key: 'workerTeam' },
    { label: 'Last Active', key: 'lastActive' },
  ];
  const ref = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);

  const {
    mutate: fetchSearchResults,
    data: rawData,
    isLoading: isSearchResultsLoading,
  } = useMutation(async () => await fetchAllSearchResults({ queries, ids }), {
    onError: async () => toast.error(`Failed to retrieve search results.`),
  });

  const { mutate: emailSearchResults, isLoading: isEmailLoading } = useMutation(
    async () => await emailResumeSearchResults({ queries, requesterEmail: user.email, ids }),
    {
      onSuccess: async () =>
        toast.info(
          `Your query has more than ${MAX_AD_HOC_CSV_SIZE} results. Your results will be emailed.`,
        ),
      onError: async () => toast.error(`Failed to retrieve search results.`),
    },
  );

  const { mutate: logEvent } = useMutation(
    async () =>
      await logResumeSearchEvent({
        type: ResumeSearchEventType.ExportToCsv,
        userId: user.id,
        data: { numExportedResults: count },
      }),
  );

  useEffect(() => {
    // only "click" on the link when the files have finished loading
    if (rawData && rawData.length > 0 && !isSearchResultsLoading) {
      ref?.current?.link.click();
    }
  }, [rawData]);

  const exportCsv = () => {
    logEvent();
    if (count <= MAX_AD_HOC_CSV_SIZE) {
      fetchSearchResults();
    } else {
      emailSearchResults();
    }
  };

  return (
    <>
      <CSVLink filename={filename} headers={csvHeaders} data={rawData ?? []} ref={ref} />
      <Button
        size="lg"
        variant="secondary"
        isLoading={isSearchResultsLoading || isEmailLoading}
        disabled={isSearchResultsLoading || isEmailLoading}
        onClick={exportCsv}
      >
        Export Users (CSV)
      </Button>
    </>
  );
};
